import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Pagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { contentService } from './content.service';
import type { Content } from './content.dto';

import { useToast } from '../../contexts/ToastContext';
const Contents = () => {
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  const [contents, setContents] = useState<Content[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedContentId, setSelectedContentId] = useState<number | null>(
    null
  );

  const itemsPerPage = 10;

  useEffect(() => {
    contentService.setLogoutCallback(logout);
  }, [logout]);

  // Debounce search term
  useEffect(() => {
    console.log('🔍 Search term changed to:', searchTerm);
    const timer = setTimeout(() => {
      console.log(
        '⏱️ Debounce complete - setting debouncedSearchTerm to:',
        searchTerm
      );
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      console.log('🧹 Clearing debounce timeout');
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Reset page when search term changes
  useEffect(() => {
    console.log('📄 Debounced search term changed, resetting page to 1');
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  const fetchContents = useCallback(async () => {
    if (!token) return;

    console.log(
      '🔄 Fetching contents - Page:',
      currentPage,
      'Search:',
      debouncedSearchTerm
    );

    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: debouncedSearchTerm || undefined,
      };

      const response = await contentService.getContents(token, params);

      if (response && response.data) {
        setContents(response.data);
        setTotalPages(response.meta?.lastPage || 1);
      } else {
        setContents([]);
        setTotalPages(1);
      }
    } catch (err) {
      console.error('Error fetching contents:', err);
      setContents([]);
      setTotalPages(1);
      showError(
        err instanceof Error ? err.message : 'Failed to fetch contents'
      );
    } finally {
      setLoading(false);
    }
  }, [token, currentPage, debouncedSearchTerm]);

  useEffect(() => {
    fetchContents();
  }, [fetchContents]);

  const handleDelete = async () => {
    if (!token || !selectedContentId) return;

    try {
      await contentService.deleteContent(token, selectedContentId);
      showSuccess('Content deleted successfully!');
      fetchContents();
    } catch (err) {
      showError(
        err instanceof Error ? err.message : 'Failed to delete content'
      );
    }
    setDeleteDialog(false);
    setSelectedContentId(null);
  };

  const handleDeleteClick = (id: number) => {
    setSelectedContentId(id);
    setDeleteDialog(true);
  };

  const handleEdit = (id: number) => {
    navigate(`/content/edit/${id}`);
  };

  const handleView = (id: number) => {
    navigate(`/content/view/${id}`);
  };

  const handleAddContent = () => {
    navigate('/content/add');
  };

  const handleToggleStatus = async (id: number) => {
    if (!token) return;

    try {
      await contentService.toggleContentStatus(token, id);
      showSuccess('Content status updated successfully!');
      fetchContents();
    } catch (err) {
      showError(
        err instanceof Error ? err.message : 'Failed to update content status'
      );
    }
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
  };

  const getStatusColor = (status: number) => {
    return status ? 'success' : 'error';
  };

  const getStatusText = (status: number) => {
    return status ? 'Active' : 'Inactive';
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4">Content Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddContent}
        >
          Add Content
        </Button>
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search content by title"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          disabled={loading}
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            ),
            endAdornment: (
              <Box display="flex" alignItems="center" gap={1}>
                {searchTerm !== debouncedSearchTerm && (
                  <CircularProgress size={20} />
                )}
                {searchTerm && (
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    disabled={loading}
                    aria-label="Clear search"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ),
          }}
        />
      </Box>

      {/* Contents Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : contents.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No content found
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Slug</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contents.map((content, index) => (
                  <TableRow key={content.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {(currentPage - 1) * 10 + index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {content.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={content.slug}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(content.status)}
                        color={getStatusColor(content.status)}
                        size="small"
                        onClick={() => handleToggleStatus(content.id)}
                        sx={{ cursor: 'pointer' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="info"
                        onClick={() => handleView(content.id)}
                        size="small"
                      >
                        <ViewIcon />
                      </IconButton>
                      <IconButton
                        color="primary"
                        onClick={() => handleEdit(content.id)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteClick(content.id)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {!loading && totalPages > 1 ? (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          ) : (
            <Box display="flex" justifyContent="center" mt={3}>
              <Typography variant="caption" color="text.secondary">
                {loading
                  ? 'Loading...'
                  : `Showing page ${currentPage} of ${totalPages}`}
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete Content</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this content? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Contents;
